import type { CSSProperties, ReactNode } from 'react';

/**
 * Placeholders in the shape of what is coming: grey blocks where the log's
 * tracks, the document's page or the table's rows will be, so the screen
 * keeps its layout while it loads instead of jumping when the content lands.
 * Each one says what it is waiting for to a screen reader.
 */

/** One grey block. */
export function Bone({ w, h = 10, style }: { w?: number | string; h?: number | string; style?: CSSProperties }) {
  return <span className="sk-bone" style={{ width: w, height: h, ...style }} aria-hidden="true" />;
}

/** Wraps a placeholder: busy for assistive technology, silent to the eye. */
function Busy({ label, className, children }: { label: string; className: string; children: ReactNode }) {
  return (
    <div className={`sk ${className}`} role="status" aria-busy="true" aria-live="polite">
      <span className="visually-hidden">{label}</span>
      {children}
    </div>
  );
}

// Widths that vary from line to line, so a block of text does not read as a grid.
const LINE_WIDTHS = ['92%', '78%', '85%', '64%', '88%', '72%', '80%', '58%'];

/** Lines of text: a list, a paragraph, a history. */
export function SkeletonLines({ lines = 4, label = 'Loading' }: { lines?: number; label?: string }) {
  return (
    <Busy label={label} className="sk-lines">
      {Array.from({ length: lines }, (_, i) => (
        <Bone key={i} w={LINE_WIDTHS[i % LINE_WIDTHS.length]} h={11} />
      ))}
    </Busy>
  );
}

/** A table: a header row and rows of cells. */
export function SkeletonTable({ rows = 6, cols = 5, label = 'Loading' }: { rows?: number; cols?: number; label?: string }) {
  return (
    <Busy label={label} className="sk-table">
      <div className="sk-row sk-row--head">
        {Array.from({ length: cols }, (_, c) => (
          <Bone key={c} h={10} w={c === 0 ? '70%' : '50%'} />
        ))}
      </div>
      {Array.from({ length: rows }, (_, r) => (
        <div className="sk-row" key={r}>
          {Array.from({ length: cols }, (_, c) => (
            <Bone key={c} h={10} w={LINE_WIDTHS[(r + c) % LINE_WIDTHS.length]} />
          ))}
        </div>
      ))}
    </Busy>
  );
}

/** Table rows inside an existing table, under its real header. */
export function SkeletonRows({ rows = 4, cols }: { rows?: number; cols: number }) {
  return (
    <>
      {Array.from({ length: rows }, (_, r) => (
        <tr className="sk-tr" key={r} aria-hidden="true">
          {Array.from({ length: cols }, (_, c) => (
            <td key={c}>
              <Bone h={10} w={c === 1 ? '90%' : LINE_WIDTHS[(r + c) % LINE_WIDTHS.length]} />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

/** A folder tree: rows stepped in, as folders sit inside folders. */
export function SkeletonTree({ rows = 12, label = 'Loading folders' }: { rows?: number; label?: string }) {
  const depth = [0, 1, 1, 1, 2, 2, 2, 1, 2, 2, 1, 2];
  return (
    <Busy label={label} className="sk-tree">
      {Array.from({ length: rows }, (_, i) => (
        <div className="sk-treerow" key={i} style={{ paddingLeft: 8 + depth[i % depth.length] * 14 }}>
          <Bone w={12} h={10} />
          <Bone w={LINE_WIDTHS[i % LINE_WIDTHS.length]} h={10} />
        </div>
      ))}
    </Busy>
  );
}

/** A document page on its grey stage. */
export function SkeletonPage({ label = 'Opening the document' }: { label?: string }) {
  return (
    <Busy label={label} className="sk-stage">
      <div className="sk-page">
        <Bone w="45%" h={14} />
        <Bone w="30%" h={10} />
        <div className="sk-gap" />
        {Array.from({ length: 6 }, (_, i) => (
          <Bone key={i} w={LINE_WIDTHS[i]} h={9} />
        ))}
        <div className="sk-gap" />
        <div className="sk-grid">
          {Array.from({ length: 12 }, (_, i) => (
            <Bone key={i} h={9} />
          ))}
        </div>
        <div className="sk-gap" />
        {Array.from({ length: 5 }, (_, i) => (
          <Bone key={i} w={LINE_WIDTHS[(i + 3) % LINE_WIDTHS.length]} h={9} />
        ))}
      </div>
    </Busy>
  );
}

/** A log: a toolbar, a header of track titles and the tracks running down. */
export function SkeletonLog({ label = "Reading the well's log data" }: { label?: string }) {
  const widths = [30, 150, 30, 90, 70, 150, 150, 90, 200];
  return (
    <Busy label={label} className="sk-log">
      <div className="sk-logbar">
        <Bone w={90} h={22} />
        <Bone w={120} h={22} />
        <Bone w={150} h={22} />
        <span className="sk-spacer" />
        <Bone w={70} h={22} />
        <Bone w={60} h={22} />
      </div>
      <div className="sk-logtracks">
        {widths.map((w, i) => (
          <div className="sk-track" key={i} style={{ width: w }}>
            <div className="sk-trackhead">
              {w > 40 && <Bone w="60%" h={10} />}
              {w > 40 && <Bone w="80%" h={6} />}
            </div>
            <div className="sk-trackbody" />
          </div>
        ))}
      </div>
    </Busy>
  );
}

/** The well dashboard: the status strip and two document panels. */
export function SkeletonDashboard({ label = 'Loading well data' }: { label?: string }) {
  return (
    <Busy label={label} className="sk-dashboard">
      <div className="sk-strip">
        {[180, 90, 90, 160, 70, 70, 70, 70, 90].map((w, i) => (
          <Bone key={i} w={w} h={34} />
        ))}
      </div>
      <div className="sk-panels">
        {[0, 1].map((i) => (
          <div className="sk-panel" key={i}>
            <div className="sk-panelhead">
              <Bone w={70} h={20} />
              <Bone w="40%" h={12} />
              <Bone w={70} h={20} />
            </div>
            <div className="sk-panelbody">
              <div className="sk-page">
                {Array.from({ length: 8 }, (_, j) => (
                  <Bone key={j} w={LINE_WIDTHS[(j + i) % LINE_WIDTHS.length]} h={9} />
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </Busy>
  );
}
